import {
  AssetClassCode,
  AssetClassMap,
  AssetSector,
  AssetSubClassCode,
  AssetSubClassMap,
  Class,
} from '../api/entity-constants';
import { Entity, MappedEntity } from '../api/entity-schema';
import { isMappedEntity, mapEntity } from '../mappers/mapEntity';

export type AssetLogoVariant =
  | 'StockInitial'
  | 'EquitySector'
  | 'FinancialInstrument'
  | 'Crypto'
  | 'FxCurrency'
  | 'Default';

export const SECTOR_TO_ICON: Record<string, string> = {
  [AssetSector.CommunicationServices]: 'SatelliteDish',
  [AssetSector.ConsumerDiscretionary]: 'TShirt',
  [AssetSector.ConsumerStaples]: 'Washer',
  [AssetSector.Energy]: 'OilCan',
  [AssetSector.Financials]: 'MoneyCheckEdit',
  [AssetSector.HealthCare]: 'HospitalAlt',
  [AssetSector.Industrials]: 'Tools',
  [AssetSector.Materials]: 'Construction',
  [AssetSector.RealEstate]: 'Sign',
  [AssetSector.Utilities]: 'FaucetDrip',
  [AssetSector.InformationTechnology]: 'ComputerClassic',
  [AssetSector.Other]: 'ChartLine',
};

export const SUB_CLASS_TO_ICON: Record<string, string> = {
  [AssetSubClassMap[AssetSubClassCode.FutureElectricity]]: 'Bolt',
  [AssetSubClassMap[AssetSubClassCode.FutureEnergy]]: 'OilCan',
  [AssetSubClassMap[AssetSubClassCode.FutureFood]]: 'Cow',
  [AssetSubClassMap[AssetSubClassCode.FutureGrains]]: 'Corn',
  [AssetSubClassMap[AssetSubClassCode.CommodityIndex]]: 'ChartLine',
  [AssetSubClassMap[AssetSubClassCode.FutureMetals]]: 'Construction',
  [AssetSubClassMap[AssetSubClassCode.FutureSofts]]: 'MugHot',
};

export const getAssetLogoVariant = (
  entity: MappedEntity
): AssetLogoVariant | undefined => {
  if (entity.class === Class.ClassStock) {
    return 'StockInitial';
  }

  if (entity.asset_class === AssetClassMap[AssetClassCode.Equity]) {
    return 'EquitySector';
  }

  if (entity.asset_class === AssetClassMap[AssetClassCode.Fi]) {
    return 'FinancialInstrument';
  }

  if (entity.sub_class === AssetSubClassMap[AssetSubClassCode.FxCrypto]) {
    return 'Crypto';
  }

  if (entity.asset_class === AssetClassMap[AssetClassCode.Fx]) {
    return 'FxCurrency';
  }

  if (entity.asset_class === AssetClassMap[AssetClassCode.Currency]) {
    return 'Default';
  }

  return undefined;
};

export type AssetLogoComputedProps = {
  variant?: AssetLogoVariant;
  isNotStockEntity: boolean;
  isFiEntity: boolean;
  iconName: string;
  subClassIcon: string;
};

export const getAssetLogoComputedProps = (
  entity: MappedEntity
): AssetLogoComputedProps => {
  const variant = getAssetLogoVariant(entity);
  const isNotStockEntity =
    entity.class !== Class.ClassStock &&
    entity.class !== Class.ClassUnspecified;
  const isFiEntity = entity.class === Class.ClassFi;
  const sectorCode = entity.gics?.sector?.code ?? '';
  const iconName =
    (sectorCode && SECTOR_TO_ICON[sectorCode]) ||
    SECTOR_TO_ICON[AssetSector.Other];
  const subClassIcon = SUB_CLASS_TO_ICON[entity.sub_class];
  return { variant, isNotStockEntity, isFiEntity, iconName, subClassIcon };
};

export type AssetLogoHelperResult = AssetLogoComputedProps & {
  entity: MappedEntity;
};

export const getAssetLogoFromEntity = (
  entity: Entity | MappedEntity
): AssetLogoHelperResult => {
  const mapped = isMappedEntity(entity) ? entity : mapEntity(entity);
  const { variant, isNotStockEntity, isFiEntity, iconName, subClassIcon } =
    getAssetLogoComputedProps(mapped);
  return {
    entity: mapped,
    variant,
    isNotStockEntity,
    isFiEntity,
    iconName,
    subClassIcon,
  };
};
